import { useFormState, useWatch } from "react-hook-form";
import PropTypes from "prop-types";
import { useParams } from "react-router";

import Options from "shared/components/inputs/form-fields/Options";
import VStack from "shared/components/layout/stack/VStack";
import Form from "shared/components/inputs/form/Form";
import TextInput from "shared/components/inputs/form-fields/TextInput";
import ModalActions from "shared/components/feedback/modals/ModalActions";

import { sendNotification } from "shared/components/feedback/notification/notification";
import FormFeedbackNotificationText from "shared/components/feedback/error/FormFeedbackNotificationText";

import useClientCampaigns from "features/admin/clients/single/useClientCampaigns";
import { useClientCampaignsApi } from "features/admin/projects/useProjects";

const attributesOptions = [{ label: "Active", value: "Active" }];

const ModalCTA = ({
    isEditing,
    isLoading,
    onClose,
    handleOnClickDeleteCampaign,
}) => {
    const { isValid } = useFormState();
    const formData = useWatch();
    return (
        <ModalActions
            primaryAction={{
                type: "submit",
                label: isEditing ? "Save" : "Add",
                disabled: !isValid,
            }}
            secondaryAction={{
                type: "button",
                label: "Cancel",
                onClick: onClose,
            }}
            {...(isEditing && {
                tertiaryAction: {
                    type: "button",
                    leadingIcon: "delete",
                    label: "Delete",
                    onClick: () => handleOnClickDeleteCampaign(formData),
                },
            })}
            as="fieldset"
            disabled={isLoading}
        />
    );
};

function transformCampaignDataToFormData(campaignData = {}) {
    return {
        name: campaignData.CampaignName,
        attributes: attributesOptions
            .map((attribute) => attribute.value)
            .filter((item) =>
                item === "Active"
                    ? campaignData[item] ?? true
                    : campaignData[item]
            )
            .join(","),
    };
}

function transformFormDataToCampaignData(
    formData = {},
    CampaignData = {},
    clientId = null,
    ClientCampaignId = undefined
) {
    const newCampaignData = { ...CampaignData };
    newCampaignData.Id = ClientCampaignId;

    newCampaignData.Name = formData.name;

    attributesOptions.forEach((attribute) => {
        newCampaignData[attribute.value] = formData.attributes
            .split(",")
            .includes(attribute.value);
    });

    newCampaignData.ParentId = clientId;

    newCampaignData.ClientId = clientId;

    return newCampaignData;
}

const ClientCampaignForm = ({ campaignIdToEdit, onClose }) => {
    const { clientId } = useParams();
    const { clientCampaigns } = useClientCampaignsApi();
    const {
        clientCampaignsByClientId,
        createClientCampaign,
        updateClientCampaign,
        deleteClientCampaign,
    } = useClientCampaigns();

    if (clientCampaignsByClientId?.isLoading) {
        return <div>Loading...</div>;
    }

    if (clientCampaignsByClientId?.error) {
        return <div>Error</div>;
    }

    const isEditing = campaignIdToEdit !== undefined;
    const modifier = isEditing ? updateClientCampaign : createClientCampaign;

    const currentClientCampaignData = clientCampaignsByClientId?.data.find(
        (campaign) => campaign.ClientCampaignId === campaignIdToEdit
    );

    const campaignsDataExceptCurrent = isEditing
        ? clientCampaigns?.data?.filter(
              (c) => c.ClientCampaignId !== campaignIdToEdit
          )
        : clientCampaigns?.data;

    if (isEditing && !currentClientCampaignData) {
        return <div>Could not find client campaign</div>;
    }

    const handleOnClickDeleteCampaign = async (prevData) => {
        // keeping active true for undo
        const restoreData = {
            ...prevData,
            attributes: attributesOptions[0].value,
        };

        const response = await deleteClientCampaign.mutateAsync({ clientCampaignId: campaignIdToEdit });
        onClose();
        sendNotification(
            <FormFeedbackNotificationText
                responseStatus={response?.status}
                isDeleting={true}
                name={currentClientCampaignData.CampaignName}
            />,
            {
                close: true,
                action: true,
                actionProps: {
                    label: "Undo",
                    onClick: () => onSubmit(restoreData),
                },
            }
        );
    };

    const onSubmit = async (formData) => {
        const clientCampaignsData = transformFormDataToCampaignData(
            formData,
            currentClientCampaignData,
            clientId,
            campaignIdToEdit
        );

        const allData = {
            clientCampaignId: campaignIdToEdit,
            body: clientCampaignsData,
        };

        const response = await modifier.mutateAsync(allData);
        onClose();
        sendNotification(
            <FormFeedbackNotificationText
                responseStatus={response?.status}
                isEditing={isEditing}
                name={clientCampaignsData.Name}
            />
        );
    };

    return (
        <Form
            onSubmit={onSubmit}
            defaultValues={transformCampaignDataToFormData(
                currentClientCampaignData
            )}
            mode="onChange"
        >
            <VStack spacing="Three">
                <VStack spacing="Two">
                    <TextInput
                        type="text"
                        name="name"
                        label="Name"
                        placeholder="Campaign Name"
                        validations={{
                            required: true,
                            validate: (value) =>
                                campaignsDataExceptCurrent?.filter(
                                    (c) =>
                                        c?.CampaignName?.toLowerCase() ===
                                        value.toLowerCase()
                                ).length === 0 || "Name is already exist",
                        }}
                    />
                    <Options name="attributes" options={attributesOptions} />
                </VStack>
                <ModalCTA
                    isEditing={isEditing}
                    handleOnClickDeleteCampaign={handleOnClickDeleteCampaign}
                    onClose={onClose}
                    isLoading={modifier.isLoading}
                />
            </VStack>
        </Form>
    );
};

ModalCTA.propTypes = {
    isEditing: PropTypes.bool,
    campaignIdToEdit: PropTypes.number,
    isLoading: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    handleOnClickDeleteCampaign: PropTypes.func
};

ClientCampaignForm.propTypes = {
    campaignIdToEdit: PropTypes.number,
    onClose: PropTypes.func.isRequired
};

export default ClientCampaignForm;
